import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { FluxMark } from "../components/flux/Decor";

const display = "font-display";

function InfoLayout({ title, testId, children }) {
  return (
    <div className="min-h-screen bg-[#0f1729] text-gray-100" data-testid={testId}>
      <nav className="border-b border-white/5 px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <Link to="/" className="text-gray-400 hover:text-[#f59e0b] transition-colors duration-200"><ArrowLeft className="w-5 h-5" /></Link>
          <FluxMark className="w-7 h-7" />
          <span className={`${display} font-bold text-lg tracking-wider uppercase text-white`}>FLU<span className="text-[#f59e0b]">X</span> <span className="font-normal text-gray-300">CAPACITOR</span></span>
        </div>
      </nav>
      <main className="max-w-3xl mx-auto px-6 py-14">
        <h1 className={`${display} text-3xl md:text-4xl font-bold text-white mb-10`}>{title}</h1>
        <div className="space-y-8">{children}</div>
        <div className="mt-14 pt-6 border-t border-white/5 flex flex-wrap gap-6 text-xs text-gray-400">
          <Link to="/faq" className="hover:text-[#f59e0b] transition-colors duration-200">FAQ</Link>
          <Link to="/privacy" className="hover:text-[#f59e0b] transition-colors duration-200">Privacy Policy</Link>
          <Link to="/terms" className="hover:text-[#f59e0b] transition-colors duration-200">Terms of Service</Link>
          <Link to="/cookies" className="hover:text-[#f59e0b] transition-colors duration-200">Cookie Policy</Link>
        </div>
      </main>
    </div>
  );
}

function Block({ q, children }) {
  return (
    <div className="bg-[#131f3a] border border-white/5 rounded-2xl p-6">
      <h2 className={`${display} text-base font-semibold text-[#f59e0b] mb-2`}>{q}</h2>
      <div className="text-sm text-gray-300 leading-relaxed space-y-2">{children}</div>
    </div>
  );
}

export function Faq() {
  return (
    <InfoLayout title="Frequently Asked Questions" testId="faq-page">
      <Block q="What is Flux Capacitor?">
        <p>Flux Capacitor is an AI-powered chronological intelligence tool. Paste messy text, upload PDFs, images, audio or video, or drop in a link — Professor Flux extracts every dated event and organizes them into parallel timeline tracks (Support, Safety, Family, Procedural).</p>
      </Block>
      <Block q="What file types can Flux ingest?">
        <p>PDFs (including scanned documents — Flux has built-in OCR), images (PNG, JPG, WEBP and more), audio files (MP3, WAV, M4A — up to 25MB), and video files (MP4, MOV and more — up to 100MB). Flux transcribes audio and video, reads documents, and extracts the timeline automatically.</p>
      </Block>
      <Block q="What does 'chronological renaming' mean?">
        <p>After Flux processes a file, it renames it with the earliest date found inside — e.g. <code className="text-[#2dd4bf]">2024-01-15_custody-order.pdf</code> — so your files sort themselves in perfect chronological order.</p>
      </Block>
      <Block q="How much does it cost?">
        <p>Every new account starts with a <strong className="text-white">free 5-day trial</strong> with full access. After that, it's <strong className="text-white">$4.99 per week</strong>. Each Weekly Pass adds 7 days of access.</p>
      </Block>
      <Block q="What are AI Insights?">
        <p>Professor Flux skims your entire timeline and generates two things: a plain-language narrative of your story (declaration-style, ready to share with a judge, advocate, or family), and pattern insights — gaps, escalations, clusters, and contradictions he notices across your events.</p>
      </Block>
      <Block q="What is the Panic Override button?">
        <p>Privacy matters. Hitting the red Panic Override button (or pressing ESC anywhere in the matrix) instantly replaces the page with Google — no history entry back to the app. Built for people documenting sensitive situations.</p>
      </Block>
      <Block q="Is my data private?">
        <p>Yes. Your events, files, and insights are scoped to your account only. Nobody else can view them. See our <Link to="/privacy" className="text-[#f59e0b]">Privacy Policy</Link> for details.</p>
      </Block>
      <Block q="Can I delete my data?">
        <p>Absolutely. Delete individual events, delete uploaded files, or wipe your entire timeline with one click ("Wipe Matrix").</p>
      </Block>
    </InfoLayout>
  );
}

export function Privacy() {
  return (
    <InfoLayout title="Privacy Policy" testId="privacy-page">
      <p className="text-xs text-gray-400 font-mono">Last updated: June 2026</p>
      <Block q="What we collect">
        <p>When you sign in with Google we receive your email address, display name, and profile picture. With email/password signup we store your email and a securely hashed password. If you link a phone number for SMS Stash, we store that number and the messages/photos you text in. When you use the app we store the content you choose to ingest: pasted text, uploaded files, links, extracted timeline events, and generated insights.</p>
      </Block>
      <Block q="How we use it">
        <p>Your content is used for exactly one purpose: building and analyzing <em>your</em> timeline. Ingested content is sent to our AI processing provider to extract events and generate insights. We do not sell, rent, or share your personal data with advertisers.</p>
      </Block>
      <Block q="Storage & security">
        <p>Events and metadata are stored in our database; uploaded files are stored on our servers, renamed chronologically. All data is scoped to your account. Three deliberate exceptions exist, each fully under your control: collaborators you invite by email, read-only report links you create, and timelines you explicitly make public. PIN-vaulted events are excluded from all of these.</p>
      </Block>
      <Block q="Public timelines & tips">
        <p>If you make a timeline public, its events (except vaulted ones) are visible to anyone with the link. Tip submitters may optionally provide a name and contact info — that information is visible only to you, the timeline admin, and is never published.</p>
      </Block>
      <Block q="Payments">
        <p>Payments are processed by Stripe. We never see or store your card number. We keep a transaction record (amount, status, session reference) to manage your access.</p>
      </Block>
      <Block q="Your rights">
        <p>You may delete any event, file, or your entire timeline at any time from within the app. To request full account deletion, contact us and we will remove your account, sessions, files, and all associated data.</p>
      </Block>
      <Block q="Sensitive situations">
        <p>We built Flux Capacitor for people who sometimes document difficult things. The Panic Override feature exists so you can leave instantly. We recommend using a device and account that only you control.</p>
      </Block>
    </InfoLayout>
  );
}

export function Terms() {
  return (
    <InfoLayout title="Terms of Service" testId="terms-page">
      <p className="text-xs text-gray-400 font-mono">Last updated: July 2026</p>
      <Block q="1. The service">
        <p>Flux Capacitor ("the Service") is an AI-powered tool that organizes user-provided content into chronological timelines. By creating an account or using the Service you agree to these Terms. If you do not agree, do not use the Service.</p>
      </Block>
      <Block q="2. Not legal advice">
        <p>Flux Capacitor is an organizational tool. Nothing it produces — timelines, summaries, insights, or answers — constitutes legal advice, and AI output may contain errors. Always verify important dates and facts against your original documents, and consult a licensed attorney for legal matters.</p>
      </Block>
      <Block q="3. Subscriptions & billing">
        <p>New accounts receive a free 5-day trial with full access. Continued access costs $4.99 per Weekly Pass (7 days), processed by Stripe. Passes are consumed as purchased and are non-refundable once the period has begun, except where required by law.</p>
      </Block>
      <Block q="4. Your content">
        <p>You retain full ownership of everything you upload or create. You grant us only the limited rights needed to operate the Service: storing your content, processing it with AI providers to extract events and generate insights, and displaying it back to you and to anyone you explicitly share it with.</p>
      </Block>
      <Block q="5. Sharing, collaborators & public timelines">
        <p>You control sharing. Report links are read-only and revocable. Collaborators you invite receive the access level you choose. If you make a timeline public, you are responsible for the content you publish and for moderating community tips before approving them. Do not publish another person's private information unlawfully.</p>
      </Block>
      <Block q="6. Acceptable use">
        <p>You agree not to use the Service to harass, stalk, or defame anyone; to upload unlawful content; to submit knowingly false tips to public timelines; to probe or disrupt the Service; or to violate any applicable law. We may suspend accounts that do.</p>
      </Block>
      <Block q="7. SMS Evidence Stash">
        <p>By linking your phone number, you consent to us receiving and processing texts and photos you send to your Flux number. Standard carrier message and data rates apply. Only link a number you own.</p>
      </Block>
      <Block q="8. Termination">
        <p>You may stop using the Service and request account deletion at any time. We may suspend or terminate accounts that violate these Terms, with notice where practicable.</p>
      </Block>
      <Block q="9. Disclaimers & liability">
        <p>The Service is provided "as is" without warranties of any kind. To the maximum extent permitted by law, our total liability for any claim related to the Service is limited to the amount you paid us in the 3 months before the claim arose.</p>
      </Block>
      <Block q="10. Changes">
        <p>We may update these Terms as the Service evolves. Material changes will be announced in the app. Continued use after changes take effect constitutes acceptance.</p>
      </Block>
    </InfoLayout>
  );
}

export function Cookies() {
  return (
    <InfoLayout title="Cookie Policy" testId="cookies-page">
      <p className="text-xs text-gray-400 font-mono">Last updated: June 2026</p>
      <Block q="Cookies we use">
        <p><strong className="text-white">session_token</strong> — an essential, httpOnly cookie that keeps you signed in for up to 7 days. Without it, the app cannot function. It contains a random session identifier only — never your password or personal data.</p>
      </Block>
      <Block q="Analytics">
        <p>We use privacy-respecting product analytics to understand how the app is used (page views, feature usage). No advertising trackers, no cross-site tracking, no data sales.</p>
      </Block>
      <Block q="Local storage">
        <p>We store your timeline skin preference in your browser's local storage so your chosen look persists between visits. It never leaves your device.</p>
      </Block>
      <Block q="Managing cookies">
        <p>You can clear cookies in your browser at any time — you'll simply be signed out. Blocking the essential session cookie will prevent sign-in from working.</p>
      </Block>
    </InfoLayout>
  );
}

